import { useState } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/homeData';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();

    if (honeypot) {
      setStatus('success');
      setMessage('Thank you for subscribing!');
      return;
    }

    if (!email.trim()) return;

    setStatus('loading');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email.trim());

      const response = await fetch('https://readdy.ai/api/form/d912q1c6ci2plld1oi0g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });

      if (response.ok) {
        setStatus('success');
        setMessage('Thank you for subscribing! Check your inbox for updates.');
        setEmail('');
      } else {
        setStatus('error');
        setMessage('Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setMessage('Something went wrong. Please try again.');
    }
  };

  return (
    <footer className="bg-background-950 text-background-50">
      <div className="px-6 md:px-10 py-14 md:py-20">
        <div className="max-w-7xl mx-auto">
          {/* Top Section */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-8">
            {/* Brand */}
            <div className="md:col-span-1">
              <Link to="/" className="text-xl font-heading font-bold tracking-tight whitespace-nowrap">
                VigorBuy
              </Link>
              <p className="mt-3 text-sm text-foreground-300 leading-relaxed">
                A curated resource for modern product discovery.
              </p>
            </div>

            {/* Categories Links */}
            <div>
              <h4 className="text-sm font-semibold text-background-50 mb-4">Categories</h4>
              <div className="grid grid-cols-2 gap-2">
                {categories.slice(0, 8).map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.link}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-foreground-300 hover:text-background-50 transition-colors whitespace-nowrap"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>

            {/* Resources */}
            <div>
              <h4 className="text-sm font-semibold text-background-50 mb-4">Resources</h4>
              <div className="flex flex-col gap-2">
                <Link to="/vigorbuy-spreadsheet-guide" className="text-sm text-foreground-300 hover:text-background-50 transition-colors whitespace-nowrap">
                  Buying Guide
                </Link>
                <a href="#safety" className="text-sm text-foreground-300 hover:text-background-50 transition-colors whitespace-nowrap">
                  Safety Tips
                </a>
                <a href="#comparison" className="text-sm text-foreground-300 hover:text-background-50 transition-colors whitespace-nowrap">
                  Compare
                </a>
                <a href="#faq" className="text-sm text-foreground-300 hover:text-background-50 transition-colors whitespace-nowrap">
                  FAQ
                </a>
              </div>
            </div>

            {/* Newsletter */}
            <div>
              <h4 className="text-sm font-semibold text-background-50 mb-4">Stay Updated</h4>
              <p className="text-sm text-foreground-300 mb-4 leading-relaxed">
                Get product updates and buying tips delivered to your inbox.
              </p>
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="flex flex-col gap-2"
              >
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="visually-hidden-field"
                />
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="w-full px-4 py-2.5 text-sm bg-background-800 border border-background-700 rounded-md text-background-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full px-4 py-2.5 text-sm font-semibold bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap disabled:opacity-60 cursor-pointer"
                >
                  {status === 'loading' ? 'Subscribing...' : 'Subscribe'}
                </button>
              </form>
              {status !== 'idle' && status !== 'loading' && (
                <p className={`mt-2 text-xs ${status === 'success' ? 'text-primary-400' : 'text-accent-400'}`}>
                  {message}
                </p>
              )}
            </div>
          </div>

          {/* Bottom */}
          <div className="mt-14 pt-8 border-t border-background-800 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-foreground-400">
              &copy; {new Date().getFullYear()} VigorBuy. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-xs text-foreground-400 hover:text-background-50 transition-colors whitespace-nowrap">
                VigorBuy Sheet
              </a>
              <Link to="/vigorbuy-spreadsheet-guide" className="text-xs text-foreground-400 hover:text-background-50 transition-colors whitespace-nowrap">
                Guide
              </Link>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-xs text-foreground-400 hover:text-background-50 transition-colors whitespace-nowrap">
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}